import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  CircularProgress,
  Chip,
  Select,
  FormControl,
  InputLabel,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  alpha,
  useTheme,
  Stack,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Send,
  Search,
  FilterList,
  Notifications,
} from '@mui/icons-material';
import { apiService } from '../services/api.service';
import { firebaseService } from '../services/firebase.service';
import { Notification } from '../types';
import Layout from '../components/Layout';

const NotificationManagement: React.FC = () => {
  const theme = useTheme();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [search, setSearch] = useState('');
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  const [newNotification, setNewNotification] = useState({
    title: '',
    message: '',
    type: 'system' as 'booking' | 'payment' | 'system' | 'promotional' | 'reminder',
    priority: 'medium' as 'low' | 'medium' | 'high',
    userRole: 'all' as 'student' | 'tutor' | 'all',
  });

  const loadNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const filters: any = {
        page: page + 1,
        limit: rowsPerPage,
        search,
      };
      if (typeFilter) filters.type = typeFilter;
      if (statusFilter) filters.status = statusFilter;

      const result = await apiService.getNotifications(filters);
      setNotifications(result.data);
      setTotal(result.pagination.total);
    } catch (error) {
      console.error('Failed to load notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, typeFilter, statusFilter, search]);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  const handleSendNotification = async () => {
    try {
      // Map admin format to Cloud Function format
      const priorityMap: Record<string, string> = {
        'low': 'normal',
        'medium': 'normal',
        'high': 'high',
      };

      // If userRole is 'all', send to both all_students and all_tutors
      if (newNotification.userRole === 'all') {
        // Send to all students
        await apiService.sendNotification({
          title: newNotification.title,
          body: newNotification.message, // Cloud Function expects 'body', not 'message'
          targetAudience: 'all_students',
          priority: priorityMap[newNotification.priority] || 'normal',
          sent: false,
          type: newNotification.type, // Keep for admin tracking
          userRole: 'student', // Keep for admin tracking
        });

        // Send to all tutors
        await apiService.sendNotification({
          title: newNotification.title,
          body: newNotification.message,
          targetAudience: 'all_tutors',
          priority: priorityMap[newNotification.priority] || 'normal',
          sent: false,
          type: newNotification.type,
          userRole: 'tutor', // Keep for admin tracking
        });

        setSnackbarMessage('Notification sent successfully to all students and tutors');
      } else {
        // Map userRole to targetAudience
        const targetAudience = newNotification.userRole === 'student' 
          ? 'all_students' 
          : 'all_tutors';

        await apiService.sendNotification({
          title: newNotification.title,
          body: newNotification.message, // Cloud Function expects 'body', not 'message'
          targetAudience: targetAudience,
          priority: priorityMap[newNotification.priority] || 'normal',
          sent: false,
          type: newNotification.type, // Keep for admin tracking
          userRole: newNotification.userRole, // Keep for admin tracking
        });

        const audienceName = newNotification.userRole === 'student' ? 'all students' : 'all tutors';
        setSnackbarMessage(`Notification sent successfully to ${audienceName}`);
      }

      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      loadNotifications();
      setSendDialogOpen(false);
      setNewNotification({
        title: '',
        message: '',
        type: 'system',
        priority: 'medium',
        userRole: 'all',
      });
    } catch (error) {
      console.error('Failed to send notification:', error);
      setSnackbarMessage('Failed to send notification. Please try again.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const getStatusStyles = (status: string) => {
    switch (status) {
      case 'sent':
        return { color: 'success.main', bg: alpha(theme.palette.success.main, 0.15) };
      case 'pending':
        return { color: 'warning.main', bg: alpha(theme.palette.warning.main, 0.15) };
      case 'failed':
        return { color: 'error.main', bg: alpha(theme.palette.error.main, 0.15) };
      default:
        return { color: 'text.secondary', bg: alpha(theme.palette.grey[500], 0.15) };
    }
  };

  const formatDate = (dateString: string) => {
    const date = firebaseService.convertTimestamp(dateString);
    return date.toLocaleString();
  };

  return (
    <Layout>
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        <Box sx={{
          mb: 4,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: 2
        }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800, mb: 1, fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>
              Notification Management
            </Typography>
            <Typography color="text.secondary" variant="body2">
              Send and manage platform notifications to users.
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<Send />}
            onClick={() => setSendDialogOpen(true)}
            sx={{ borderRadius: 3 }}
          >
            Send Notification
          </Button>
        </Box>

        <Paper sx={{ borderRadius: 4, overflow: 'hidden', boxShadow: theme.shadows[2] }}>
          <Box sx={{ p: { xs: 2, sm: 3 }, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', borderBottom: 1, borderColor: 'divider' }}>
            <TextField
              sx={{ flex: { xs: '1 1 100%', sm: 1 }, minWidth: { xs: '100%', sm: 300 } }}
              placeholder="Search notifications..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              InputProps={{ startAdornment: <Search sx={{ color: 'text.secondary', mr: 1 }} /> }}
              size="small"
            />
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 160 } }}>
              <InputLabel>Type</InputLabel>
              <Select
                value={typeFilter}
                onChange={(e) => { setTypeFilter(e.target.value); setPage(0); }}
                label="Type"
                sx={{ borderRadius: 3 }}
                IconComponent={FilterList}
              >
                <MenuItem value="">All Types</MenuItem>
                <MenuItem value="booking">Booking</MenuItem>
                <MenuItem value="payment">Payment</MenuItem>
                <MenuItem value="system">System</MenuItem>
                <MenuItem value="promotional">Promotional</MenuItem>
                <MenuItem value="reminder">Reminder</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 160 } }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
                label="Status"
                sx={{ borderRadius: 3 }}
                IconComponent={FilterList}
              >
                <MenuItem value="">All Statuses</MenuItem>
                <MenuItem value="sent">Sent</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: theme.palette.mode === 'light' ? 'grey.50' : alpha(theme.palette.common.white, 0.04) }}>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase' }}>Title</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase' }}>Message</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase' }}>Type</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase' }}>Target</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase' }}>Priority</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase' }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase' }}>Sent At</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {notifications.map((notification) => {
                      const status = notification.sent === true ? 'sent' :
                                    notification.sent === false ? 'pending' :
                                    notification.status || 'pending';
                      const statusStyle = getStatusStyles(status);
                      return (
                        <TableRow key={notification.id} hover>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {notification.title}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {notification.message || notification.body || ''}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip label={notification.type} size="small" sx={{ fontSize: '0.7rem' }} />
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={
                                notification.targetAudience === 'all_students' ? 'All Students' :
                                notification.targetAudience === 'all_tutors' ? 'All Tutors' :
                                notification.targetAudience === 'specific_users' ? 'Specific Users' :
                                notification.userRole === 'all' ? 'All Users' :
                                notification.userRole || 'All'
                              } 
                              size="small" 
                              sx={{ fontSize: '0.7rem' }} 
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={notification.priority}
                              size="small"
                              color={notification.priority === 'high' ? 'error' : notification.priority === 'medium' ? 'warning' : 'default'}
                              sx={{ fontSize: '0.7rem' }}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={
                                notification.sent === true ? 'sent' :
                                notification.sent === false ? 'pending' :
                                notification.status || 'pending'
                              }
                              size="small"
                              sx={{
                                fontWeight: 800,
                                fontSize: '0.625rem',
                                bgcolor: statusStyle.bg,
                                color: statusStyle.color,
                                borderRadius: 1.5,
                                textTransform: 'uppercase',
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">
                              {notification.sentAt ? formatDate(notification.sentAt) : 'Not sent'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                sx={{ borderTop: 1, borderColor: 'divider' }}
              />
            </>
          )}
        </Paper>

        <Dialog
          open={sendDialogOpen}
          onClose={() => setSendDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 4 } }}
        >
          <DialogTitle sx={{ fontWeight: 800 }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Notifications />
              <Typography variant="h6">Send Notification</Typography>
            </Stack>
          </DialogTitle>
          <DialogContent>
            <Stack spacing={3} sx={{ mt: 1 }}>
              <TextField
                fullWidth
                label="Title"
                value={newNotification.title}
                onChange={(e) => setNewNotification({ ...newNotification, title: e.target.value })}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
              <TextField
                fullWidth
                label="Message"
                value={newNotification.message}
                onChange={(e) => setNewNotification({ ...newNotification, message: e.target.value })}
                multiline
                rows={4}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={newNotification.type}
                  onChange={(e) => setNewNotification({ ...newNotification, type: e.target.value as any })}
                  label="Type"
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="system">System</MenuItem>
                  <MenuItem value="booking">Booking</MenuItem>
                  <MenuItem value="payment">Payment</MenuItem>
                  <MenuItem value="promotional">Promotional</MenuItem>
                  <MenuItem value="reminder">Reminder</MenuItem>
                </Select>
              </FormControl>
              <FormControl>
                <InputLabel>Target Audience</InputLabel>
                <Select
                  value={newNotification.userRole}
                  onChange={(e) => setNewNotification({ ...newNotification, userRole: e.target.value as any })}
                  label="Target Audience"
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="all">All Users</MenuItem>
                  <MenuItem value="student">Students Only</MenuItem>
                  <MenuItem value="tutor">Tutors Only</MenuItem>
                </Select>
              </FormControl>
              <FormControl>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={newNotification.priority}
                  onChange={(e) => setNewNotification({ ...newNotification, priority: e.target.value as any })}
                  label="Priority"
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSendDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleSendNotification}
              variant="contained"
              disabled={!newNotification.title.trim() || !newNotification.message.trim()}
              sx={{ borderRadius: 2 }}
            >
              Send Notification
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={4000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setSnackbarOpen(false)}
            severity={snackbarSeverity}
            icon={<Notifications />}
            sx={{ borderRadius: 3, fontWeight: 600 }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Box>
    </Layout>
  );
};

export default NotificationManagement;

